import React, { useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import Box from '../../../components/Styled/Box';
import IconButton from '../../../components/Buttons/IconButton';
import Button from '../../../components/Buttons/Button';
import DropdownMenu from '../../../components/Buttons/DropdownMenu/DropdownMenu';

import { authSelectors, userSelectors, applicationSelectors } from '../../../redux/selectors';
import { applicationActions } from '../../../redux/actions';

import { useTheme, useRoutes, useIsMobileLayout } from '../../../hooks';
import { useIsTabletLayout } from '../../../hooks/useWindowSize';

import useHeaderUtils from './useHeaderUtils';
import useHeaderMenuDropdown from './useHeaderMenuDropdown';

import Logo from '../../../assets/imgs/logo-negative.svg';
import MiniLogo from '../../../assets/imgs/logo.symbol.white.svg';

import { defaultLanguage, languages } from '../../../config/maps';

const sortAlphaNum = (a, b, locale) => a.name.localeCompare(b.name, locale, { numeric: true });

const defaultPropsDropdownMenu = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
  transformOrigin: { vertical: 'top', horizontal: 'right' },
  menuItemProps: { styles: { root: { minWidth: 200 } } },
  style: { transform: 'translateY(10px)' },
};

const getAssessmentRoutes = (routes) => ([
  routes.manager.organizationalAssessments,
  routes.manager.individualAssessments,
  routes.manager.selfAssessments,
  routes.respondent.organizationalAssessments,
  routes.respondent.individualAssessments,
]);

const Header = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const routes = useRoutes();
  const isMobile = useIsMobileLayout();
  const isTablet = useIsTabletLayout();
  const dispatch = useDispatch();
  const { formatMessage: f } = useIntl();

  const activeLanguage = useSelector(userSelectors.activeLanguage);
  const companies = useSelector(userSelectors.companies).sort((a, b) => sortAlphaNum(a, b, activeLanguage));
  const currentCompany = useSelector(userSelectors.currentCompany);
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const userHasAcceptedTerms = useSelector(userSelectors.userHasAcceptedLegalAgreement);
  const userIsManager = useSelector(userSelectors.userIsManager);
  const userIsOwner = useSelector(userSelectors.userIsOwner);
  const viewMode = useSelector(applicationSelectors.viewMode);
  const { avatar_url, first_name, last_name } = useSelector(userSelectors.user);

  const assessmentRoutes = useMemo(() => getAssessmentRoutes(routes), [routes]);
  const isAssessmentPage = assessmentRoutes.some((assessment) => pathname.includes(assessment));

  const getDefaultLanguage = useCallback((d) => d.id === defaultLanguage, []);

  const getActiveLanguage = useCallback(
    (d) => d.id === activeLanguage || d.language === activeLanguage,
    [activeLanguage],
  );

  const toggleViewMode = useCallback(() => {
    if (viewMode === 'manager') {
      dispatch(applicationActions.toggleRespondentView());
    }
    else {
      dispatch(applicationActions.toggleManagerView());
    }
  }, [dispatch, viewMode]);

  const HOME_PAGE = useMemo(() => {
    switch (viewMode) {
    case 'manager':
      return routes.manager.organizationalAssessments;

    case 'respondent':
      return routes.respondent.organizationalAssessments;

    default:
      return routes.manager.organizationalAssessments;
    }
  }, [routes, viewMode]);

  const currentLanguage = useMemo(
    () => languages.find(getActiveLanguage) || languages.find(getDefaultLanguage),
    [getActiveLanguage, getDefaultLanguage],
  );

  const userAvatar = useMemo(() => ({
    alt: `${first_name} ${last_name}`,
    src: avatar_url && `${avatar_url}?${new Date().getTime()}`,
  }), [avatar_url, first_name, last_name]);

  const { contextButtonProps } = useHeaderUtils({
    isAuthenticated,
    userIsManager,
    isManagerView: viewMode === 'manager',
    currentCompany,
  });

  const options = useHeaderMenuDropdown({
    companies,
    currentCompany,
    isAuthenticated,
    isManagerView: viewMode === 'manager',
    isAssessmentPage,
    userIsOwner,
    routes,
  });

  const LanguagesDropdown = (
    <DropdownMenu {...defaultPropsDropdownMenu} menu={options.languagesMenu}>
      <Button
        p='6px'
        variant='dark'
        size='sm'
        image={currentLanguage.image}
        rightIcon={{ icon: 'chevron-down' }}
        imageProps={{ style: { width: isMobile ? 24 : 28, height: isMobile ? 24 : 28 } }}
      />
    </DropdownMenu>
  );

  const CompanyDropdown = userIsManager && (
    <>
      <DropdownMenu {...defaultPropsDropdownMenu} menu={options.companiesMenu}>
        <Button
          size='sm'
          py='12px'
          height='40px'
          mr='2px'
          variant='dark'
          rightIcon={{ icon: 'chevron-down' }}
          leftIcon={{ icon: contextButtonProps.icon }}

          {...(userIsOwner || currentCompany) && { borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
        >
          {contextButtonProps.text}
        </Button>
      </DropdownMenu>

      {(userIsOwner || currentCompany) && (
        <DropdownMenu {...defaultPropsDropdownMenu} menu={options.managerConfig}>
          <Button
            variant='dark'
            size='sm'
            py='12px'
            typographyProps={{ fontSize: 14 }}
            leftIcon={{ icon: 'cog' }}
            rightIcon={{ icon: 'chevron-down', ml: 0 }}
            borderTopLeftRadius={0}
            borderBottomLeftRadius={0}
          />
        </DropdownMenu>
      )}
    </>
  );

  const UserDropdown = (
    <>
      <DropdownMenu {...defaultPropsDropdownMenu} menu={options.userMenu}>
        <Button
          size='sm'
          py='6px'
          pl='6px'
          mr={userIsManager ? '2px' : 0}
          variant='dark'
          rightIcon={{ icon: 'chevron-down' }}
          avatar={userAvatar}
          {...userIsManager && { borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
        >
          {first_name}
        </Button>
      </DropdownMenu>

      {userIsManager && (
        <Button
          onClick={toggleViewMode}
          variant='dark'
          size='sm'
          typographyProps={{ fontSize: 14 }}
          leftIcon={{ icon: 'random' }}
          borderTopLeftRadius={0}
          borderBottomLeftRadius={0}
        >
          {f({ id: 'layout_header.choose_view_mode' }, { isManagerView: viewMode === 'manager' })}
        </Button>
      ) }
    </>
  );

  return (
    <Box.Header
      position='fixed'
      height={60}
      width='100%'
      p={[3, 4]}
      display='flex'
      alignItems='center'
      justifyContent={(isAuthenticated && !isMobile) ? 'space-between' : 'center'}
      boxShadow={theme.shadows.light[1]}
      backgroundColor='darkblue100'
      style={{ zIndex: 1100 }}
    >
      {(isAuthenticated && !isMobile) && (
        <Link to={userHasAcceptedTerms ? HOME_PAGE : routes.termsConfirmation}>
          <img alt='logo-humanizadas.svg' src={isTablet ? MiniLogo : Logo} height='32px' />
        </Link>
      )}

      {(!isAuthenticated || isMobile) && (
        <img alt='logo-humanizadas.svg' src={Logo} height='32px' />
      )}

      {(isAuthenticated && userHasAcceptedTerms && !isMobile) && (
        <Box
          ml={3}
          display='flex'
          position={isMobile ? 'absolute' : 'static'}
          right={isMobile ? 0 : 'auto'}
        >
          <Link to={HOME_PAGE}>
            <Button
              height='40px'
              variant='dark'
              size='sm'
              leftIcon={{ icon: ['far', 'clipboard'] }}
              disabled={isAssessmentPage}
            >
              {f({ id: 'layout_header.assessments' })}
            </Button>
          </Link>

          {CompanyDropdown && (
            <Box display='flex' ml={3}>
              {CompanyDropdown}
            </Box>
          )}

          {UserDropdown && (
            <Box display='flex' ml={3}>
              {UserDropdown}
            </Box>
          )}

          {LanguagesDropdown && (
            <Box display='flex' ml={3}>
              {LanguagesDropdown}
            </Box>
          )}

          <DropdownMenu {...defaultPropsDropdownMenu} menu={options.menu} boxProps={{ ml: 3 }}>
            <IconButton icon='question-circle' variant='dark' />
          </DropdownMenu>
        </Box>
      )}

      {!isAuthenticated && (
        <Box display='flex' position='absolute' right={0}>
          {LanguagesDropdown}
        </Box>
      )}
    </Box.Header>
  );
};

export default Header;
